import React, { useState, useEffect } from "react";
import {
  getPortfolio,
  createPortfolio,
  updatePortfolio,
  deletePortfolio,
} from "../../services/portfolioService";

export default function ManagePortfolio() {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    title: "",
    category: "",
    tech_stack: "",
    description: "",
    image: null,
  });

  // ✅ Fetch projects on load
  useEffect(() => {
    document.title = "Manage Portfolio | Development Solutions";
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getPortfolio();
      setProjects(data);
    } catch (err) {
      console.error("❌ Failed to fetch projects:", err);
    }
  };

  // ✅ Open Add/Edit Form
  const openForm = (proj = null) => {
    if (proj) {
      setFormData({
        id: proj.id,
        title: proj.title,
        category: proj.category,
        tech_stack: proj.tech_stack || "",
        description: proj.description || "",
        image: null,
      });
    } else {
      setFormData({
        id: null,
        title: "",
        category: "",
        tech_stack: "",
        description: "",
        image: null,
      });
    }
    setShowForm(true);
  };

  // ✅ Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle Image Upload
  const handleImageChange = (e) => {
    setFormData((prev) => ({ ...prev, image: e.target.files[0] }));
  };

  // ✅ Save Project (Create/Update)
  const saveProject = async () => {
    setLoading(true);

    const data = new FormData();
    data.append("title", formData.title ?? "");
    data.append("category", formData.category ?? "");
    data.append("tech_stack", formData.tech_stack ?? "");
    data.append("description", formData.description ?? "");
    if (formData.image) data.append("image", formData.image);

    const token = localStorage.getItem("adminToken");

    try {
      if (formData.id) {
        await updatePortfolio(formData.id, data, token);
      } else {
        await createPortfolio(data, token);
      }
      fetchProjects();
      setShowForm(false);
    } catch (error) {
      console.error("❌ Save Error:", error.response?.data || error);
      alert("❌ Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete Project
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    const token = localStorage.getItem("adminToken");

    try {
      await deletePortfolio(id, token);
      fetchProjects();
    } catch (error) {
      console.error("❌ Delete Error:", error);
      alert("❌ Failed to delete project");
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-bold">📂 Manage Portfolio</h2>
        <button
          onClick={() => openForm()}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          ➕ Add Project
        </button>
      </div>

      {/* ✅ Projects Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-2 text-left">Title</th>
              <th className="px-4 py-2 text-left">Category</th>
              <th className="px-4 py-2 text-left">Tech Stack</th>
              <th className="px-4 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length > 0 ? (
              projects.map((proj) => (
                <tr key={proj.id} className="border-t">
                  <td className="px-4 py-2">{proj.title}</td>
                  <td className="px-4 py-2">{proj.category}</td>
                  <td className="px-4 py-2">{proj.tech_stack || "-"}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => openForm(proj)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(proj.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center p-4 text-gray-500">
                  No projects yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl">
            <h3 className="text-xl font-bold mb-4">
              {formData.id ? "Edit Project" : "Add Project"}
            </h3>

            {/* Title */}
            <label className="block mb-2 font-semibold">Title *</label>
            <input
              name="title"
              className="w-full p-2 border rounded mb-4"
              value={formData.title}
              onChange={handleChange}
            />

            {/* Category */}
            <label className="block mb-2 font-semibold">Category *</label>
            <input
              name="category"
              className="w-full p-2 border rounded mb-4"
              value={formData.category}
              onChange={handleChange}
            />

            {/* Tech Stack */}
            <label className="block mb-2 font-semibold">Tech Stack</label>
            <input
              name="tech_stack"
              className="w-full p-2 border rounded mb-4"
              value={formData.tech_stack}
              onChange={handleChange}
            />

            {/* Description */}
            <label className="block mb-2 font-semibold">Description</label>
            <textarea
              name="description"
              className="w-full p-2 border rounded mb-4"
              value={formData.description}
              onChange={handleChange}
            />

            {/* ✅ Show existing image preview if editing */}
            {formData.id &&
              projects.find((p) => p.id === formData.id)?.image && (
                <div className="mb-4">
                  <p className="font-semibold mb-1">Current Image:</p>
                  <img
                    src={`${process.env.REACT_APP_API_URL.replace("/api", "")}/${
                      projects.find((p) => p.id === formData.id).image
                    }`}
                    alt="Current Project"
                    className="w-full h-40 object-cover rounded shadow"
                  />
                </div>
              )}

            {/* Image Upload */}
            <label className="block mb-2 font-semibold">Image</label>
            <input
              type="file"
              accept="image/*"
              className="mb-4"
              onChange={handleImageChange}
            />

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={saveProject}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}